import { Grid } from '@mui/material';
import { Duration } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { SettingsBody, SettingsData, SettingsHead } from '~/components/settings';
import { StripedCard } from '~/components/striped-card';
import { TagInput } from '~/components/tag';
import { useAppContext } from '~/contexts';
import { TaggableType } from '~/generated/graphql';
import { formatDate } from '~/lib/datetime';
import { unCamelize } from '~/lib/string';
import { playerSupports } from '~/pages/devices/lib/player-version';
import { displaySpace, displayTemperature, formatUptime } from '../lib';
import type { Device } from '../types';
import { DeviceNotes } from './DeviceNotes';
import { NetworkingPanel } from './networking-panel';

const NETWORKING_METADATA_KEY = 'networking';

const commandSchema = z.object({
  command: z.string(),
  arbitrary: z.string().optional(),
});

const FRIENDLY_TZ: Record<string, string> = {
  'America/New_York': 'Eastern',
  'America/Chicago': 'Central',
  'America/Denver': 'Mountain',
  'America/Los_Angeles': 'Pacific',
  'America/Juneau': 'Alaska',
  'Pacific/Honolulu': 'Hawaii',
};

interface AdditionalProps {
  device: Device;
}

export const Additional = ({ device }: AdditionalProps) => {
  const { t, i18n } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();
  const command = commandSchema.parse(
    device.command || { command: t('pages.devices.show.additional.na') },
  );

  const metadataGrid = useMemo(() => {
    if (!device.metadata) return [];
    return Object.entries(device.metadata)
      .filter(([key]) => key !== NETWORKING_METADATA_KEY)
      .map(([key, value]) => {
        const translationKey = unCamelize(key);
        const label = i18n.exists(`pages.devices.show.metadata.${translationKey}`)
          ? t(`pages.devices.show.metadata.${translationKey}`)
          : translationKey;

        return (
          <Grid item xs={12} md={4} key={key}>
            <StripedCard content={value} label={label} />
          </Grid>
        );
      });
  }, [device.metadata, i18n, t]);

  return (
    // A wrapped column line is sized to its widest item, which would let long
    // values (e.g. switch names) stretch these panels past the column.
    <Grid container direction="column" flexWrap="nowrap" spacing={2}>
      <Grid item>
        <SettingsHead>{t('common.labels.general')}</SettingsHead>
        <SettingsBody>
          <SettingsData className="last">
            <Grid container spacing={2}>
              {currentUser.admin && (
                <Grid item xs={12}>
                  <DeviceNotes deviceId={device.id} notes={device.notes} />
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <StripedCard
                  content={device.id.toString()}
                  label={t('pages.devices.show.additional.id')}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StripedCard
                  content={FRIENDLY_TZ[device.timeZone] || device.timeZone}
                  label={t('pages.devices.show.additional.time_zone')}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StripedCard
                  content={command.arbitrary || command.command}
                  label={t('pages.devices.show.additional.command')}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StripedCard
                  content={formatDate(device.activationChangedAt)}
                  label={t('pages.devices.show.additional.activated')}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StripedCard
                  content={
                    device.statusUpdatedAt
                      ? formatDate(device.statusUpdatedAt)
                      : t('pages.devices.show.additional.na')
                  }
                  label={t('pages.devices.show.additional.status_updated')}
                />
              </Grid>
              {currentUser.admin && (
                <>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      content={
                        device.activeShow?.show.services.map((x) => x.name).join(', ') ||
                        t('pages.devices.show.additional.na')
                      }
                      label={t('pages.devices.show.additional.services')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      content={
                        device.screenshotInterval
                          ? Duration.fromISO(device.screenshotInterval)
                              .reconfigure({ locale: i18n.language })
                              .toHuman()
                          : t('pages.devices.show.additional.na')
                      }
                      label={t('pages.devices.show.additional.screenshot_interval')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      content={device.overscan}
                      label={t('pages.devices.show.additional.overscan')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      content={t(
                        `pages.devices.show.settings.nightly_behavior.${device.nightlyBehavior.toLowerCase()}`,
                      )}
                      label={t('pages.devices.show.additional.nightly_reboot')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      content={device.tvBrand?.name ?? t('pages.devices.show.additional.na')}
                      label={t('common.labels.tv_brand')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      content={t(`common.boolean.${device.eventStream}`)}
                      label={t('pages.devices.show.additional.event_stream')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      content={t(`common.boolean.${device.realtime}`)}
                      label={t('pages.devices.show.additional.real_time')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      content={t(`common.boolean.${device.internal}`)}
                      label={t('common.labels.internal')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      content={t(`common.boolean.${device.demo}`)}
                      label={t('common.labels.demo')}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} md={4}>
                <StripedCard
                  content={`${device.grabInterval} ${t('pages.devices.show.additional.seconds')}`}
                  label={t('pages.devices.show.additional.content_fetch_interval')}
                />
              </Grid>
              {playerSupports(device.playerVersion, 'statusInterval') && (
                <Grid item xs={12} md={4}>
                  <StripedCard
                    content={`${device.statusInterval} ${t(
                      'pages.devices.show.additional.seconds',
                    )}`}
                    label={t('pages.devices.show.additional.status_report_interval')}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <StripedCard
                  content={
                    device.currentChannel
                      ? `${device.currentChannel.number}: ${device.currentChannel.name}`
                      : t('pages.devices.show.additional.na')
                  }
                  label={t('pages.devices.show.additional.current_channel')}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StripedCard
                  content={device.currentView?.name ?? t('pages.devices.show.additional.na')}
                  label={t('pages.devices.show.additional.current_view')}
                />
              </Grid>
              {currentNetwork.canManage.value && (
                <Grid item xs={12} md={4}>
                  <StripedCard
                    content={t(`common.boolean.${device.closedCaptioning}`)}
                    label={t('pages.devices.show.additional.closed_captioning')}
                  />
                </Grid>
              )}
              {/* TODO: don't show for content editor, needs fix */}
              {device.canUpdate.value && (
                <Grid item xs={12}>
                  <TagInput
                    taggableId={device.id}
                    taggableType={TaggableType.Device}
                    tags={device.tags.map((x) => x)}
                  />
                </Grid>
              )}
            </Grid>
          </SettingsData>
        </SettingsBody>
      </Grid>
      {device.networking && <NetworkingPanel networking={device.networking} />}
      {currentNetwork.canManage.value && (
        <>
          <Grid item>
            <SettingsHead>{t('pages.devices.show.additional.metrics')}</SettingsHead>
            <SettingsBody>
              <SettingsData className="last">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      content={formatUptime(device.processUptime)}
                      label={t('pages.devices.show.additional.software_uptime')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      content={formatUptime(device.osUptime)}
                      label={t('pages.devices.show.additional.hardware_uptime')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      content={
                        device.cpuPercentage
                          ? `${device.cpuPercentage}%`
                          : t('pages.devices.show.additional.na')
                      }
                      label={t('pages.devices.show.additional.cpu_usage')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      content={displayTemperature(device.cpuTemperature)}
                      label={t('pages.devices.show.additional.cpu_temperature')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      content={displaySpace(device.diskAvailable, device.diskTotal)}
                      label={t('pages.devices.show.additional.disk')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StripedCard
                      content={displaySpace(device.memoryAvailable, device.memoryTotal)}
                      label={t('pages.devices.show.additional.memory')}
                    />
                  </Grid>
                </Grid>
              </SettingsData>
            </SettingsBody>
          </Grid>
          {metadataGrid.length > 0 && (
            <Grid item>
              <SettingsHead>{t('pages.devices.show.additional.metadata')}</SettingsHead>
              <SettingsBody>
                <SettingsData className="last">
                  <Grid container spacing={2}>
                    {metadataGrid}
                  </Grid>
                </SettingsData>
              </SettingsBody>
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};
